// A2F-766: authenticated /configure surface, on the v2 configurator.
//
// Replaces the A2F-670 ConfiguratorCore-driven version. Both the
// signup flow at `/` and this editor render the same
// ConfiguratorV2Body so a returning user sees the surface they signed
// up on. The page-level fork is Section 6: signup form on `/`,
// SectionSaveEdit (Save button + status line) here.
//
// State flow:
//   mount → getProfile() → hydrate initialState → ConfiguratorV2Body
//   renders → onStateChange streams ConfiguratorV2State back → dirty
//   bit derives from state inequality vs the hydrated baseline → save
//   button assembles a Profile via buildProfile and PUTs to
//   /api/v1/ribbon/profile.
//
// Dirty bit: state-divergence-based, not callback-ordering-based. The
// v1 implementation used a `hasBaselined` ref that flipped on the
// first onStateChange call; per A2F-766 we compare each streamed
// state against the hydrated baseline so the dirty signal reflects
// actual user edits. Resetting the baseline after save success
// returns the page to "Saved" without inventing a new dirty fact.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import * as api from '../lib/api'
import type { FormatRule, Profile, Rule, TableStylePreset } from '../types'
import { PACKS } from '../data/packs'
import {
  ConfiguratorV2Body,
  DEFAULT_PACKS,
  enabledPackIds,
  mergeProfileRules,
  type ConfiguratorV2InitialState,
  type ConfiguratorV2State,
} from '../components/configurator-v2/ConfiguratorV2Body'
import { SectionCard } from '../components/configurator-v2/SectionCard'
import {
  SectionSaveEdit,
  type SaveStatus,
} from '../components/configurator-v2/SectionSaveEdit'
import type { PacksState } from '../components/onboard/v2/PacksList'
import {
  applyPills,
  formattersFromState,
  stateFromFormatters,
  tableStylesChanged,
  type CustomRuleChip,
  type FormattingPill,
} from '../lib/onboard-v2-formatting'
import {
  BASE_FORMATTING,
  DEFAULT_TABLE_STYLES,
  type FormattingFields,
} from '../data/onboard-v2-fields'
import { buildProfile as assembleProfile } from '../lib/profile-assembly'
import {
  formatRuleToTableStylePreset,
  isTableStyleFormatRule,
  tableStylePresetToFormatRule,
} from '../lib/table-style-mapping'
import { Telemetry } from '../lib/telemetry'
import type { ReferralSource } from '../lib/telemetry-types'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'

const DOMAIN = 'biotech_ectd'

// A2F-997: read-time compat. Prefer format rules from profile.rules when
// present; fall back to legacy formatters.tableStyles for pre-Phase-1 profiles.
// Converts format rules back to TableStylePreset[] so SectionTableStyles can
// keep its existing UI-friendly knobs without a shape change.
function hydrateTableStyles(
  rules: Rule[],
  legacyPresets: TableStylePreset[] | undefined,
): TableStylePreset[] {
  const tableRules = rules.filter(isTableStyleFormatRule)
  if (tableRules.length > 0) {
    return tableRules.map((r) => formatRuleToTableStylePreset(r as FormatRule))
  }
  return legacyPresets ?? DEFAULT_TABLE_STYLES
}

function inferReferralSource(): ReferralSource {
  try {
    const ref = typeof document !== 'undefined' ? document.referrer : ''
    if (!ref) return 'direct'
    if (ref.includes('commons.chronabio.ai')) return 'ctdcommons'
    if (ref.includes('chronabio.ai')) return 'referral'
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

function hydratePacksFromProfile(
  profilePacks: string[],
  profileDisabledPacks: string[],
): PacksState {
  // A2F-796: a pack is effectively ON iff it's in profile.packs AND
  // NOT in profile.disabledPacks. The taskpane reads disabledPacks
  // for the OFF state; /configure needs to mirror that or the toggle
  // UI lies about the actual state (e.g. taskpane shows pack OFF,
  // /configure shows it ON, user toggles ON-then-OFF in /configure
  // trying to "fix it", nothing changes because the pack id was
  // already in profile.packs but stays in disabledPacks).
  const disabled = new Set(profileDisabledPacks)
  const isEffectivelyOn = (id: string) => profilePacks.includes(id) && !disabled.has(id)
  return {
    indSectionHeadings: true,
    coverLetterValidation: isEffectivelyOn('ind_v0'),
    fdaTerminology: isEffectivelyOn('fda_writing_standards_v0'),
    // A2F-801: ectd_formatting_v0 is now a real registered pack;
    // hydrate from profile state instead of pinning to false.
    ectdFormatting: isEffectivelyOn('ectd_formatting_v0'),
    // A2F-803: nda_reviewers_guide_v0 — same shape as ectdFormatting.
    ndaReviewersGuide: isEffectivelyOn('nda_reviewers_guide_v0'),
  }
}

function hydrateCustomRules(
  rules: Rule[],
  profilePacks: string[],
  profileDisabledPacks: string[],
): CustomRuleChip[] {
  // Pack-contributed rules are not "custom" — exclude them so the
  // Custom rules section only renders rules the user added by hand.
  //
  // A2F-797: only effectively-enabled packs claim their rule ids.
  // Iterating profilePacks unconditionally — including ids the user
  // has disabled via My Chrona's pack-grouped surface — would exclude
  // the disabled pack's rules from Custom while the /configure UI
  // also shows the pack toggle as off (no rule rendering). For a
  // legacy/tagged profile where, say, fda_writing_standards_v0
  // overlaps with the search_terminology_fda tag, that rule then
  // disappears from /configure entirely, and an unrelated save drops
  // it from profile.rules. Skipping disabled packs here keeps the
  // overlap visible under Custom so the next save preserves it.
  const disabled = new Set(profileDisabledPacks)
  const packRuleIds = new Set<string>()
  for (const packId of profilePacks) {
    if (disabled.has(packId)) continue
    const pack = PACKS[packId]
    if (!pack) continue
    for (const rid of pack.ruleIds) packRuleIds.add(rid)
  }
  // A2F-997: also exclude table-style format rules — they're managed by the
  // SectionTableStyles editor, not the custom-rules input, and would appear
  // as confusing "Custom rules" entries in the UI if left in.
  return rules
    .filter((r) => !packRuleIds.has(r.id) && !isTableStyleFormatRule(r))
    .map((r) => ({
      id: r.id,
      label: r.label,
      status: 'active' as const,
      rule: r,
    }))
}

interface BaselineSnapshot {
  manualEdits: FormattingFields
  packs: PacksState
  customRules: CustomRuleChip[]
  tableStyles: TableStylePreset[]
  pills: FormattingPill[]
  tags: string[]
  // A2F-796: tracked so handleSave can detect a "user re-enabled a
  // pack" save action (cleaned disabledPacks differs from this
  // baseline value) without having to compare against the live
  // server state. snapshotsEqual deliberately doesn't compare this
  // field — /configure has no UI that mutates it, so user-driven
  // dirty-bit logic stays unchanged.
  disabledPacks: string[]
}

function snapshotsEqual(
  a: BaselineSnapshot,
  b: ConfiguratorV2State,
): boolean {
  if (!shallowEqualKeyed(a.manualEdits, b.manualEdits)) return false
  if (!shallowEqualKeyed(a.packs as unknown as Record<string, unknown>, b.packs as unknown as Record<string, unknown>)) return false
  if (a.tags.length !== b.tags.length) return false
  for (let i = 0; i < a.tags.length; i++) if (a.tags[i] !== b.tags[i]) return false
  if (a.pills.length !== b.pills.length) return false
  for (let i = 0; i < a.pills.length; i++) {
    if (a.pills[i].id !== b.pills[i].id) return false
  }
  if (a.customRules.length !== b.customRules.length) return false
  for (let i = 0; i < a.customRules.length; i++) {
    const ac = a.customRules[i]
    const bc = b.customRules[i]
    if (ac.id !== bc.id || ac.status !== bc.status) return false
  }
  // Table styles change in shape often (rename, reorder, recolor) so
  // delegate to the existing per-field comparator. Compare against
  // each other rather than DEFAULT_TABLE_STYLES.
  if (tableStylesChanged(a.tableStyles, b.tableStyles)) return false
  return true
}

function shallowEqualKeyed(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const ak = Object.keys(a)
  const bk = Object.keys(b)
  if (ak.length !== bk.length) return false
  for (const k of ak) {
    if (a[k] !== b[k]) return false
  }
  return true
}

export function ConfiguratorPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [initial, setInitial] = useState<ConfiguratorV2InitialState | null>(null)
  const [baseline, setBaseline] = useState<BaselineSnapshot | null>(null)
  const [latestState, setLatestState] = useState<ConfiguratorV2State | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  // Snapshot of latestState at the moment of a failed save attempt.
  // While `latestState` references this exact same object, the user
  // hasn't edited since the failure → 'error' is sticky. Once the
  // user makes an edit, latestState's ref changes and we transition
  // to 'dirty' (so a click on the Save button re-saves the new
  // state, not the old failed one).
  const failedSnapshotRef = useRef<ConfiguratorV2State | null>(null)

  // ── Mount: hydrate from backend, fire telemetry ─────────────────────
  useEffect(() => {
    Telemetry.setScreen('/configure')
    Telemetry.configuratorViewed({ source: inferReferralSource() })
    Telemetry.configuratorPathLoaded({ path: 'edit' })
    let cancelled = false
    api
      .getProfile()
      .then((p) => {
        if (cancelled) return
        const profilePacks = p.packs ?? []
        const profileDisabledPacks = p.disabledPacks ?? []
        const init: ConfiguratorV2InitialState = {
          manualEdits: p.formatters ? stateFromFormatters(p.formatters) : {},
          packs: hydratePacksFromProfile(profilePacks, profileDisabledPacks),
          customRules: hydrateCustomRules(p.rules, profilePacks, profileDisabledPacks),
          tableStyles: hydrateTableStyles(p.rules, p.formatters?.tableStyles),
          tags: p.tags ?? [],
          // A2F-796: carry disabledPacks through to latestState so
          // handleSave can compute a "user re-enabled a pack" diff
          // against baseline — toggling fdaTerminology false→true in
          // /configure must clear that pack from the disabledPacks
          // list, otherwise the merge preserves the OFF state and the
          // /configure action looks broken.
          disabledPacks: profileDisabledPacks,
        }
        setInitial(init)
        setBaseline({ ...init, pills: [], disabledPacks: profileDisabledPacks })
        setSaveStatus('saved')
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        const status = (err as { status?: number } | null)?.status
        if (status === 404) {
          // No saved profile yet — render with FRE defaults so a
          // stranded returning user can build one and save.
          const init: ConfiguratorV2InitialState = {
            manualEdits: {},
            packs: DEFAULT_PACKS,
            customRules: [],
            tableStyles: DEFAULT_TABLE_STYLES,
            tags: [],
            disabledPacks: [],
          }
          setInitial(init)
          setBaseline({ ...init, pills: [], disabledPacks: [] })
          setSaveStatus('saved')
          setLoading(false)
          return
        }
        setError("Couldn't load your profile. Check your connection and retry.")
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStateChange = useCallback((state: ConfiguratorV2State) => {
    setLatestState(state)
  }, [])

  // Dirty derives from state inequality vs the hydrated baseline.
  // Save success resets baseline → dirty flips back to false naturally.
  // Save errors do NOT reset baseline; instead the failed snapshot is
  // captured in failedSnapshotRef so 'error' stays sticky until the
  // user actually edits past it.
  useEffect(() => {
    if (!baseline || !latestState) return
    if (saveStatus === 'saving') return
    setSaveStatus((prev) => {
      if (prev === 'error') {
        // 'error' is sticky while latestState matches the failed
        // snapshot ref. Only transition to 'dirty' once the user
        // edits past the failure (latestState reference moves on).
        if (failedSnapshotRef.current === latestState) return 'error'
        return 'dirty'
      }
      const dirty = !snapshotsEqual(baseline, latestState)
      return dirty ? 'dirty' : 'saved'
    })
  }, [baseline, latestState, saveStatus])

  const handleSave = useCallback(async () => {
    if (!latestState) return
    // Capture the snapshot we're attempting to save. If the request
    // fails, this becomes the failedSnapshotRef target — the dirty
    // effect uses it to keep 'error' sticky until the user edits past
    // this exact reference.
    const stateAtAttempt = latestState
    setSaveStatus('saving')
    try {
      // A2F-796: a user toggling a previously-disabled pack ON in
      // /configure should re-enable the pack. The fdaTerminology
      // toggle ON ⇒ pack lands in enabledPackIds; if it was in
      // disabledPacks at hydrate time, clear it so the effective
      // state actually flips. Computed against the hydrated baseline
      // so we only write the field when the configurator action
      // changed it — this minimizes (but doesn't eliminate) the
      // cross-tab race window. If My Chrona toggled an unrelated
      // pack OFF between this session's hydrate and save, that
      // change can be lost on the response — see the Linear ticket
      // for the known limitation.
      const enabled = enabledPackIds(latestState.packs)
      const cleanedDisabledPacks = (baseline?.disabledPacks ?? []).filter(
        (p) => !enabled.includes(p),
      )
      const disabledPacksChanged =
        cleanedDisabledPacks.length !== (baseline?.disabledPacks ?? []).length ||
        cleanedDisabledPacks.some((p, i) => p !== (baseline?.disabledPacks ?? [])[i])

      // A2F-997: convert table style presets to format rules and merge into the
      // rules list. The dedup guard prevents collisions with pack/custom rule ids
      // (unlikely but safe). During Phase 1, table styles are dual-written: as
      // format rules here (rules[] = authoritative read source) AND as palette
      // presets via deriveFormatters below (formatters.tableStyles = pre-Phase-1
      // rollback safety — see that function's comment for why both are needed).
      const packCustomRules = mergeProfileRules(latestState.packs, latestState.customRules)
      const packCustomIds = new Set(packCustomRules.map((r) => r.id))
      const tableFormatRules = latestState.tableStyles
        .map((p) => tableStylePresetToFormatRule(p))
        .filter((r) => !packCustomIds.has(r.id))
      const allRules = [...packCustomRules, ...tableFormatRules]

      const profile: Profile = assembleProfile({
        domain: DOMAIN,
        // tags pass-through — preserved exactly as hydrated; the
        // editor doesn't expose a tag picker, but a legacy
        // pre-redesign profile may carry tag keys we shouldn't drop.
        tags: latestState.tags,
        packs: enabled,
        rules: allRules,
        formatters: deriveFormatters(latestState),
        // Send disabledPacks only when /configure's action mutated it
        // (i.e. user re-enabled a previously-disabled pack). When
        // unchanged, omit so backend partial-merge preserves whatever
        // the server currently has — keeps My Chrona toggles made
        // since hydrate intact.
        ...(disabledPacksChanged ? { disabledPacks: cleanedDisabledPacks } : {}),
      })
      const resp = await api.saveProfile(DOMAIN, profile)
      // Reset the baseline so dirty derivation flips back to false.
      // Keep the same `pills` shape — they don't round-trip through
      // saveProfile (manualEdits absorbs their effect via formatters)
      // so the post-save baseline matches what comes out of the
      // configurator now.
      setBaseline({
        manualEdits: stateAtAttempt.manualEdits,
        packs: stateAtAttempt.packs,
        customRules: stateAtAttempt.customRules,
        tableStyles: stateAtAttempt.tableStyles,
        pills: stateAtAttempt.pills,
        tags: stateAtAttempt.tags,
        // A2F-796: post-save baseline reflects the server-echoed
        // disabledPacks (resp.disabledPacks already has any cleared
        // entries removed if we sent the field). Fallback to the
        // attempt's snapshot if the server response somehow drops it.
        disabledPacks: resp.disabledPacks ?? stateAtAttempt.disabledPacks,
      })
      failedSnapshotRef.current = null
      setSaveStatus('saved')
      Telemetry.configuratorProfileSaved({
        rule_count: resp.rules.length,
        manifest_version: resp.manifestVersion,
      })
    } catch (e) {
      console.error('save failed', e)
      // Sticky — record the failed snapshot so the dirty-effect
      // keeps 'error' until the user edits past this exact state.
      // Their unsaved state is preserved in latestState; nothing's
      // been lost.
      failedSnapshotRef.current = stateAtAttempt
      setSaveStatus('error')
    }
  }, [latestState])

  const saveStateLabel = useMemo(() => {
    switch (saveStatus) {
      case 'saving':
        return { text: 'Saving…', tone: 'orange' as const }
      case 'error':
        return { text: 'Save failed — retry', tone: 'red' as const }
      case 'dirty':
        return { text: 'Unsaved changes', tone: 'orange' as const }
      case 'saved':
      case 'idle':
      default:
        return { text: 'Saved', tone: 'gray' as const }
    }
  }, [saveStatus])

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    )
  }

  if (error || !initial) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="max-w-sm text-center space-y-3">
          <p className="text-sm text-slate-600">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-brand-orange text-white px-4 py-2 text-sm font-medium hover:bg-brand-orange/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-brand-canvas text-brand-charcoal font-sans">
      <ConfiguratorV2Body
        mode="edit"
        initialState={initial}
        onStateChange={handleStateChange}
        renderInstallSection={({
          summary,
          isOpen,
          onToggle,
          edited,
          onFocus,
        }) => (
          <SectionCard
            id="install"
            number={6}
            title="Save your configuration"
            subtitle="Updates apply on the next Word session — no re-sideload."
            isOpen={isOpen}
            edited={edited}
            onToggle={onToggle}
            onFocus={onFocus}
            statusOverride={
              <span className="flex flex-col items-end gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-brand-orange/10 text-brand-orange">
                  {summary.ribbonButtons} buttons · {summary.packs}{' '}
                  {summary.packs === 1 ? 'pack' : 'packs'} ·{' '}
                  {summary.customRules}{' '}
                  {summary.customRules === 1 ? 'rule' : 'rules'}
                </span>
                <SaveStateBadge
                  tone={saveStateLabel.tone}
                  text={saveStateLabel.text}
                  onClickRetry={
                    saveStatus === 'error' ? handleSave : undefined
                  }
                />
              </span>
            }
          >
            <SectionSaveEdit status={saveStatus} onSave={handleSave} />
          </SectionCard>
        )}
      />
      <TermsPrivacyFooter />
    </div>
  )
}

// Mirror of ConfiguratorV2Body's internal formatters derivation so the
// save body matches what the user sees. Replays BASE ⊕ pills ⊕ manualEdits
// the same way the body does.
function deriveFormatters(state: ConfiguratorV2State): Profile['formatters'] {
  const formattingState: FormattingFields = {
    ...applyPills(BASE_FORMATTING, state.pills),
    ...state.manualEdits,
  }
  const fromFlat = formattersFromState(formattingState)
  // A2F-997: write tableStyles into formatters as well as rules[] during the
  // Phase 1 bake period. The backend upsert does a SHALLOW top-level merge
  // ({**existing, **put_body}), so whenever `formatters` is present in the PUT
  // body it replaces the entire object — including any legacy tableStyles. If
  // we stop writing tableStyles here, a save with any non-table formatting
  // change would silently wipe the user's table styles from the JSONB, making
  // rollback to pre-Phase-1 binaries lossy. Phase 2 cleanup can drop this once
  // the migration has run and the Phase-1 binaries are retired.
  const tablesEdited = tableStylesChanged(state.tableStyles, DEFAULT_TABLE_STYLES)
  if (tablesEdited) return { ...(fromFlat ?? {}), tableStyles: state.tableStyles }
  return fromFlat
}

interface SaveStateBadgeProps {
  tone: 'gray' | 'orange' | 'red'
  text: string
  onClickRetry?: () => void
}

function SaveStateBadge({ tone, text, onClickRetry }: SaveStateBadgeProps) {
  const cls =
    tone === 'red'
      ? 'text-red-600'
      : tone === 'orange'
        ? 'text-brand-orange'
        : 'text-slate-500'

  if (onClickRetry) {
    // Split the label so only "retry" is the click target. Rendered
    // as a role=button span (not a real <button>) because the
    // surrounding SectionCard header is itself a <button> and nested
    // buttons are invalid HTML. stopPropagation prevents the header
    // toggle from firing on retry click.
    const idx = text.toLowerCase().lastIndexOf('retry')
    if (idx >= 0) {
      const head = text.slice(0, idx)
      const retryWord = text.slice(idx)
      return (
        <span className={`text-[10px] font-medium ${cls}`}>
          {head}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onClickRetry()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onClickRetry()
              }
            }}
            className="underline font-semibold cursor-pointer"
          >
            {retryWord}
          </span>
        </span>
      )
    }
  }
  return <span className={`text-[10px] font-medium ${cls}`}>{text}</span>
}
